/**
 * StatusHistory — Public 90-day audit archive of the pytest suite.
 * Sprint Feb 2026 — "SOC2-lite" trust layer. Feeds off the same redacted
 * /api/status/history endpoint that security auditors can pull directly.
 *
 * Two headline affordances:
 *   1. Verifiable JSON snapshot download (SHA-256 in the response header
 *      AND embedded in the JSON body so auditors can spot-check integrity
 *      without ever touching HiBFF's dashboard).
 *   2. Scrollable, month-grouped timeline of every recorded run.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, CheckCircle2, XCircle, Loader2, ShieldCheck, Copy, FlaskConical, Clock, FileText,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_WINDOWS = [
  { value: 30,  label: '30d' },
  { value: 90,  label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '1y' },
];

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  } catch (_) { return ''; }
}

function formatDateLong(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch (_) { return iso; }
}

function monthKey(iso) {
  if (!iso) return 'Unknown';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  } catch (_) { return 'Unknown'; }
}

const StatusHistory = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [snapshotStatus, setSnapshotStatus] = useState({ busy: false, hash: null, err: null });
  const [pdfStatus, setPdfStatus] = useState({ busy: false, err: null });
  const [copied, setCopied] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await fetch(`${API_URL}/api/status/history?days=${days}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setData(await resp.json());
    } catch (e) {
      setErr(e?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const downloadSnapshot = async () => {
    if (snapshotStatus.busy) return;
    setSnapshotStatus({ busy: true, hash: null, err: null });
    try {
      const resp = await fetch(`${API_URL}/api/status/history/snapshot?days=${days}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const hash = resp.headers.get('x-hibff-sha256') || null;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `hibff-ci-history-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setSnapshotStatus({ busy: false, hash, err: null });
    } catch (e) {
      setSnapshotStatus({ busy: false, hash: null, err: e?.message || 'Snapshot failed' });
    }
  };

  const copyHash = async () => {
    if (!snapshotStatus.hash) return;
    try {
      await navigator.clipboard.writeText(snapshotStatus.hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard blocked */ }
  };

  const downloadTrustReport = async () => {
    if (pdfStatus.busy) return;
    setPdfStatus({ busy: true, err: null });
    try {
      const resp = await fetch(`${API_URL}/api/status/trust-report.pdf?days=${days}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `hibff-trust-report-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setPdfStatus({ busy: false, err: null });
    } catch (e) {
      setPdfStatus({ busy: false, err: e?.message || 'PDF download failed' });
    }
  };

  // Group runs by month for the scrollable timeline.
  const grouped = useMemo(() => {
    if (!data?.runs) return [];
    const map = new Map();
    for (const r of data.runs) {
      const k = monthKey(r.ran_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    // Newest month first; preserve chronological order inside each group (Mongo already sorted desc).
    return Array.from(map.entries());
  }, [data]);

  // Cell → month-block scroller. Called from `DailyCoverageStrip` when a
  // user clicks a day. If the day's month is in the timeline, smooth-scroll
  // to it and apply a short-lived ring highlight so the eye lands on it.
  const [highlightMonth, setHighlightMonth] = useState(null);
  const scrollToDay = useCallback((ymd) => {
    if (!ymd) return;
    // Parse YMD as UTC to match how monthKey renders (avoids off-by-one on
    // near-midnight timezones).
    const iso = `${ymd}T12:00:00Z`;
    const monthLabel = monthKey(iso);
    const tid = `history-month-${monthLabel.replace(/\s/g, '-').toLowerCase()}`;
    const el = document.querySelector(`[data-testid="${tid}"]`);
    if (!el) {
      // Silent day — no month block to scroll to. Nudge the timeline
      // header into view so at least the user's context lands somewhere useful.
      const header = document.querySelector('[data-testid="history-timeline"]');
      if (header) header.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightMonth(null);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightMonth(monthLabel);
    // Highlight fades after 1.8s so the ring doesn't linger past the eye's landing.
    window.setTimeout(() => setHighlightMonth((m) => (m === monthLabel ? null : m)), 1800);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white" data-testid="history-loader">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="status-history-page">
      <PageMeta
        slug="status-history"
        path="/status/history"
        title="HiBFF Status · CI Audit Archive"
        description="90-day pytest history with verifiable SHA-256 audit snapshots. Public, no login required."
      />

      <header className="px-6 sm:px-12 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/status" className="flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="history-back-status">
          <ArrowLeft size={18} /> <span className="font-medium">Back to status</span>
        </Link>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1" data-testid="history-window-toggle">
          {DAY_WINDOWS.map(w => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition ${
                days === w.value ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`history-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      <main className="px-6 sm:px-12 max-w-5xl mx-auto pb-20">
        {/* Hero */}
        <section
          className="rounded-2xl p-8 sm:p-10 mb-8 border shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #EEF2FF 0%, #FDF2F8 100%)',
            borderColor: '#C7D2FE',
          }}
          data-testid="history-hero"
        >
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical size={18} className="text-indigo-600" />
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">CI audit archive</p>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: '#1A1209' }}>
            {stats.total_runs > 0
              ? `${stats.pass_rate}% pass rate · last ${data.window_days} days`
              : 'Awaiting first recorded run'}
          </h1>
          <p className="text-sm text-gray-600 mt-3 max-w-2xl leading-relaxed">
            Every entry below is a live pytest run against production code — safety guardrails,
            payment paths, auth flows, and Vault operations. Downloadable as a SHA-256 verifiable
            JSON snapshot for auditors, school procurement teams, or the ICO.
          </p>
          {err && (
            <p className="text-xs text-red-600 mt-3" data-testid="history-error">{err}</p>
          )}
        </section>

        {/* Stats grid */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="history-stats-grid">
          <MiniStat label="Runs" value={stats.total_runs ?? 0} tid="history-stat-runs" />
          <MiniStat label="Green" value={stats.green_runs ?? 0} tone="ok" tid="history-stat-green" />
          <MiniStat label="Red" value={stats.red_runs ?? 0} tone={stats.red_runs > 0 ? 'bad' : 'ok'} tid="history-stat-red" />
          <MiniStat label="Green streak" value={`${stats.green_streak ?? 0}`} tone="ok" tid="history-stat-streak" />
        </section>

        {/* Daily coverage strip — 30-day grid so auditors can spot silent
            days (amber) at a glance without counting dots. */}
        <DailyCoverageStrip runs={data?.runs || []} onCellClick={scrollToDay} />

        {/* Verifiable snapshot */}
        <section
          className="bg-white rounded-2xl border-2 p-5 sm:p-6 mb-10"
          style={{ borderColor: '#8B5CF6' }}
          data-testid="history-snapshot-panel"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={18} className="text-violet-600" />
                <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">Verifiable audit snapshot</p>
              </div>
              <p className="text-sm font-bold text-gray-900">Download a SHA-256 signed JSON of every run in this window.</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed max-w-xl">
                The response ships the digest in the <code className="text-[11px] bg-gray-100 px-1 rounded">X-HiBFF-SHA256</code> header
                AND embedded in the JSON body. Auditors can re-hash the canonicalised payload
                (<code className="text-[11px] bg-gray-100 px-1 rounded">sort_keys=True, separators=(",", ":")</code>) locally to
                confirm integrity — no HiBFF login required.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadSnapshot}
              disabled={snapshotStatus.busy}
              className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition"
              data-testid="history-snapshot-download"
            >
              {snapshotStatus.busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {snapshotStatus.busy ? 'Preparing…' : `Download ${data?.window_days ?? days}d snapshot`}
            </button>
          </div>

          {/* PDF trust report — same data, but formatted as a 1-page leave-behind
              for schools, journalists, board members. */}
          <div className="mt-5 pt-4 border-t border-gray-100 flex items-start justify-between gap-4 flex-wrap" data-testid="history-pdf-row">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={16} className="text-indigo-600" />
                <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">One-page trust report · PDF</p>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed max-w-xl">
                Auto-generated A4 leave-behind for schools, journalists, and investors — live badge,
                pass-rate ring, key stats, embedded SHA-256, and a QR code back to this page.
                Same data as the JSON snapshot, but formatted for humans.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadTrustReport}
              disabled={pdfStatus.busy}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition"
              data-testid="history-pdf-download"
            >
              {pdfStatus.busy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
              {pdfStatus.busy ? 'Rendering…' : 'Download PDF report'}
            </button>
          </div>
          {pdfStatus.err && (
            <p className="mt-2 text-xs text-red-600" data-testid="history-pdf-err">{pdfStatus.err}</p>
          )}

          {snapshotStatus.hash && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap" data-testid="history-snapshot-hash">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-0.5">SHA-256 of your download</p>
                <code className="text-[11px] font-mono text-gray-800 break-all leading-tight block">{snapshotStatus.hash}</code>
              </div>
              <button
                type="button"
                onClick={copyHash}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                  copied ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-700'
                }`}
                data-testid="history-snapshot-copy-hash"
              >
                <Copy size={13} /> {copied ? 'Copied' : 'Copy hash'}
              </button>
            </div>
          )}

          {snapshotStatus.err && (
            <p className="mt-3 text-xs text-red-600" data-testid="history-snapshot-err">{snapshotStatus.err}</p>
          )}
        </section>

        {/* Timeline */}
        <section data-testid="history-timeline">
          <h2 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Clock size={18} className="text-gray-500" />
            Run timeline
          </h2>
          {grouped.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 px-6 py-10 text-center text-gray-500" data-testid="history-empty">
              <FlaskConical size={32} className="mx-auto text-gray-400 mb-2" />
              <p>No runs recorded in this window.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(([month, runs]) => {
                const highlighted = highlightMonth === month;
                return (
                <div
                  key={month}
                  data-testid={`history-month-${month.replace(/\s/g, '-').toLowerCase()}`}
                  className={`rounded-2xl transition-all ${highlighted ? 'ring-2 ring-indigo-400 ring-offset-2 ring-offset-indigo-50' : ''}`}
                  style={{ scrollMarginTop: '80px' }}
                >
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">{month} · {runs.length} run{runs.length !== 1 ? 's' : ''}</p>
                  <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 shadow-sm">
                    {runs.map((r, i) => {
                      const green = r.status === 'green';
                      const Icon = green ? CheckCircle2 : XCircle;
                      return (
                        <div
                          key={i}
                          className="px-5 py-3 flex items-center gap-4"
                          data-testid={`history-run-${i}`}
                        >
                          <Icon size={22} className={green ? 'text-emerald-500' : 'text-red-500'} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              {r.passed} passed
                              {r.failed > 0 && <span className="ml-2 text-red-600">{r.failed} failed</span>}
                              {r.skipped > 0 && <span className="ml-2 text-gray-500 font-normal">· {r.skipped} skipped</span>}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {formatDateLong(r.ran_at)} · {timeAgo(r.ran_at)}
                              {r.duration_s != null && <> · {Math.round(r.duration_s)}s</>}
                            </p>
                          </div>
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: green ? '#DCFCE7' : '#FEE2E2',
                              color: green ? '#166534' : '#991B1B',
                            }}
                          >
                            {r.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </section>

        <footer className="mt-12 text-center text-xs text-gray-500">
          <p>
            Programmatic access:{' '}
            <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">GET /api/status/history?days=90</code>
            {' '}·{' '}
            <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">GET /api/status/history/snapshot</code>
            {' '}·{' '}
            <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">GET /api/status/trust-report.pdf</code>
          </p>
          <p className="mt-2">
            Report security issues to{' '}
            <a href="mailto:security@hibff.com" className="text-purple-600 hover:underline">security@hibff.com</a>.
          </p>
        </footer>
      </main>
    </div>
  );
};

const MiniStat = ({ label, value, tone, tid }) => {
  const palette = tone === 'ok'
    ? { bg: '#ECFDF5', color: '#059669' }
    : tone === 'bad'
      ? { bg: '#FEF2F2', color: '#DC2626' }
      : { bg: '#F3F4F6', color: '#1F2937' };
  return (
    <div className="rounded-xl border border-gray-100 px-4 py-3" style={{ backgroundColor: palette.bg }} data-testid={tid}>
      <p className="text-[10px] uppercase tracking-wide text-gray-600">{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-0.5" style={{ color: palette.color }}>{value}</p>
    </div>
  );
};

// Bucket the run list into UTC days, then render a 30-square strip so
// auditors can see silent days (AMBER = "no run recorded") at a glance
// without counting dots. Colour ladder mirrors the SVG badge:
//   Green   #10B981 — ≥1 run that day, all green
//   Red     #DC2626 — ≥1 red run that day
//   Amber   #D97706 — no runs recorded that day (staleness)
//   Slate   #94A3B8 — future / today-not-yet-run (last cell only)
const DAILY_DAYS = 30;

function ymdUtc(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch (_) { return null; }
}

const DailyCoverageStrip = ({ runs, onCellClick }) => {
  // Build a Map<YYYY-MM-DD, {green:int, red:int}>
  const buckets = new Map();
  for (const r of runs || []) {
    const day = ymdUtc(r.ran_at);
    if (!day) continue;
    const cur = buckets.get(day) || { green: 0, red: 0 };
    if (r.status === 'green') cur.green += 1; else cur.red += 1;
    buckets.set(day, cur);
  }

  const today = new Date();
  const todayYmd = today.toISOString().slice(0, 10);
  const cells = [];
  for (let i = DAILY_DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const ymd = d.toISOString().slice(0, 10);
    const b = buckets.get(ymd);
    let tone;
    let title;
    if (b) {
      if (b.red > 0) {
        tone = 'red';
        title = `${ymd} · ${b.red} failing · ${b.green} green`;
      } else {
        tone = 'green';
        title = `${ymd} · ${b.green} green`;
      }
    } else if (ymd === todayYmd) {
      // Today, no run YET — neutral slate, not amber (day isn't over).
      tone = 'slate';
      title = `${ymd} · no run yet today`;
    } else {
      tone = 'amber';
      title = `${ymd} · no run recorded`;
    }
    cells.push({ ymd, tone, title });
  }

  const totals = {
    green: cells.filter(c => c.tone === 'green').length,
    red: cells.filter(c => c.tone === 'red').length,
    amber: cells.filter(c => c.tone === 'amber').length,
    slate: cells.filter(c => c.tone === 'slate').length,
  };
  const coverage = Math.round(((totals.green + totals.red) / DAILY_DAYS) * 100);
  const paletteFor = (tone) => ({
    green: '#10B981',
    red: '#DC2626',
    amber: '#D97706',
    slate: '#CBD5E1',
  }[tone]);

  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-8" data-testid="history-daily-coverage">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-0.5">Daily coverage · last 30 days</p>
          <p className="text-sm text-gray-900">
            <span className="font-bold tabular-nums" data-testid="history-coverage-pct">{coverage}%</span>
            <span className="text-gray-500"> of days have a recorded run</span>
            {totals.amber > 0 && (
              <> · <span className="text-amber-700 font-semibold" data-testid="history-coverage-silent">{totals.amber} silent day{totals.amber !== 1 ? 's' : ''}</span></>
            )}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">Click any day to jump to its month in the timeline.</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <LegendDot color={paletteFor('green')} label="Green" />
          <LegendDot color={paletteFor('red')} label="Red" />
          <LegendDot color={paletteFor('amber')} label="Silent" />
          <LegendDot color={paletteFor('slate')} label="Today" />
        </div>
      </div>
      <div className="flex items-center gap-1" data-testid="history-coverage-strip">
        {cells.map((c, i) => {
          const clickable = c.tone === 'green' || c.tone === 'red';
          return (
            <button
              key={c.ymd}
              type="button"
              title={clickable ? `${c.title} · click to jump` : c.title}
              onClick={() => onCellClick && onCellClick(c.ymd)}
              disabled={!clickable && !onCellClick}
              aria-label={c.title}
              className={`flex-1 h-8 rounded-sm transition ${clickable ? 'cursor-pointer hover:opacity-80 hover:scale-y-110' : 'cursor-default opacity-70'} focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1`}
              style={{
                backgroundColor: paletteFor(c.tone),
                boxShadow: c.tone === 'slate' ? 'inset 0 0 0 1.5px #94A3B8, inset 0 0 0 3px #fff' : 'none',
              }}
              data-testid={`history-coverage-cell-${i}`}
              data-tone={c.tone}
              data-ymd={c.ymd}
              data-clickable={clickable ? 'true' : 'false'}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[9px] text-gray-400 uppercase tracking-wide">
        <span>{cells[0].ymd}</span>
        <span>today</span>
      </div>
    </section>
  );
};

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: color }} />
    {label}
  </span>
);

export default StatusHistory;
